import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { UserPlus, Phone, Lock, ArrowLeft, CheckCircle, Eye, EyeOff } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ActivateMember = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { toast } = useToast();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [memberInfo, setMemberInfo] = useState(null);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const handleCheckPhone = async (e) => {
    e.preventDefault();
    if (!phoneNumber || phoneNumber.length < 8) {
      toast({
        title: 'Erreur',
        description: 'Veuillez entrer un numéro de téléphone valide',
        variant: 'destructive'
      });
      return;
    }

    setLoading(true);
    try {
      let formattedPhone = phoneNumber.replace(/\s/g, '');
      if (!formattedPhone.startsWith('+')) {
        formattedPhone = '+225' + formattedPhone;
      }

      const response = await fetch(`${API_URL}/api/auth/check-member-phone/${encodeURIComponent(formattedPhone)}`);
      const data = await response.json();

      if (data.can_activate) {
        setMemberInfo(data);
        setStep(2);
      } else if (data.reason === 'has_account') {
        toast({
          title: 'Compte existant',
          description: data.message,
        });
        setTimeout(() => navigate('/login'), 2000);
      } else {
        toast({
          title: 'Non trouvé',
          description: data.message,
          variant: 'destructive'
        });
      }
    } catch (error) {
      /* error logged */
      toast({
        title: 'Erreur',
        description: 'Impossible de vérifier le numéro. Réessayez.',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleActivateAccount = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast({
        title: 'Erreur',
        description: 'Le mot de passe doit contenir au moins 6 caractères',
        variant: 'destructive'
      });
      return;
    }

    if (password !== confirmPassword) {
      toast({
        title: 'Erreur',
        description: 'Les mots de passe ne correspondent pas',
        variant: 'destructive'
      });
      return;
    }

    setLoading(true);
    try {
      let formattedPhone = phoneNumber.replace(/\s/g, '');
      if (!formattedPhone.startsWith('+')) {
        formattedPhone = '+225' + formattedPhone;
      }

      const response = await fetch(`${API_URL}/api/auth/activate-member-account`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          phone_number: formattedPhone,
          password: password,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        toast({
          title: 'Succès!',
          description: data.message,
        });
        // Auto login
        if (data.access_token) {
          login(data.access_token, data.user);
          navigate('/profile');
        } else {
          navigate('/login');
        }
      } else {
        toast({
          title: 'Erreur',
          description: data.detail || 'Activation échouée',
          variant: 'destructive'
        });
      }
    } catch (error) {
      /* error logged */
      toast({
        title: 'Erreur',
        description: "Impossible d'activer le compte. Réessayez.",
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#2d5a4d] via-[#235043] to-[#1a4038] flex items-center justify-center p-6">
      <Card className="w-full max-w-md p-8">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => step === 1 ? navigate('/login') : setStep(1)}
            className="p-0"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="w-12 h-12 bg-[#2d5a4d] rounded-lg flex items-center justify-center">
            <UserPlus className="w-7 h-7 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Activer mon accès web</h1>
            <p className="text-sm text-gray-600">
              {step === 1 
                ? 'Entrez le numéro enregistré par votre coopérative'
                : 'Créez votre mot de passe pour accéder au dashboard'}
            </p>
          </div>
        </div>

        {/* Progress */}
        <div className="flex items-center justify-center gap-2 mb-6">
          <div className={`w-3 h-3 rounded-full ${step >= 1 ? 'bg-[#2d5a4d]' : 'bg-gray-300'}`} />
          <div className={`w-16 h-1 ${step >= 2 ? 'bg-[#2d5a4d]' : 'bg-gray-300'}`} />
          <div className={`w-3 h-3 rounded-full ${step >= 2 ? 'bg-[#2d5a4d]' : 'bg-gray-300'}`} />
        </div>

        {/* Step 1: Phone Verification */}
        {step === 1 && (
          <form onSubmit={handleCheckPhone} className="space-y-4">
            <div>
              <Label htmlFor="phone">Numéro de téléphone</Label>
              <div className="flex mt-1">
                <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-100 text-gray-500 text-sm">
                  +225
                </span>
                <Input
                  id="phone"
                  type="tel"
                  placeholder="07 XX XX XX XX"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  className="rounded-l-none"
                  maxLength={12}
                  data-testid="member-phone-input"
                />
              </div>
            </div>

            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <p className="text-sm text-green-800">
                Utilisez le même numéro que celui enregistré par votre coopérative lors de votre adhésion.
              </p>
            </div>

            <Button
              type="submit"
              className="w-full bg-[#2d5a4d] hover:bg-[#1a4038] text-white py-6"
              disabled={loading}
              data-testid="verify-phone-btn"
            >
              {loading ? 'Vérification...' : 'Vérifier mon numéro'}
            </Button>
          </form>
        )}

        {/* Step 2: Create Password */}
        {step === 2 && memberInfo && (
          <form onSubmit={handleActivateAccount} className="space-y-4">
            {/* Member Info Card */}
            <div className="bg-green-50 p-4 rounded-lg border-l-4 border-green-600">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <span className="font-semibold text-green-800">Profil trouvé!</span>
              </div>
              <div className="space-y-1 text-sm">
                <p><span className="text-gray-500">Nom:</span> <span className="font-medium">{memberInfo.member_name}</span></p>
                <p><span className="text-gray-500">Coopérative:</span> <span className="font-medium">{memberInfo.cooperative_name}</span></p>
                {memberInfo.village && (
                  <p><span className="text-gray-500">Village:</span> <span className="font-medium">{memberInfo.village}</span></p>
                )}
              </div>
            </div>

            <div>
              <Label htmlFor="password">Créer un mot de passe</Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  placeholder="Mot de passe (6 caractères min.)"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 pr-10"
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-3"
                >
                  {showPassword ? <EyeOff className="w-5 h-5 text-gray-400" /> : <Eye className="w-5 h-5 text-gray-400" />}
                </button>
              </div>
            </div>

            <div>
              <Label htmlFor="confirmPassword">Confirmer le mot de passe</Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                <Input
                  id="confirmPassword"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  placeholder="Répétez le mot de passe"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="pl-10"
                  data-testid="confirm-password-input"
                />
              </div>
            </div>

            <Button
              type="submit"
              className="w-full bg-[#2d5a4d] hover:bg-[#1a4038] text-white py-6"
              disabled={loading}
              data-testid="activate-account-btn"
            >
              {loading ? 'Activation...' : 'Activer mon compte'}
            </Button>
          </form>
        )}

        {/* Footer */}
        <div className="mt-6 text-center">
          <p className="text-sm text-gray-600">
            Vous avez déjà un compte?{' '}
            <Link to="/login" className="text-[#2d5a4d] font-semibold hover:underline">
              Se connecter
            </Link>
          </p>
        </div>
      </Card>
    </div>
  );
};

export default ActivateMember;
